/* One screen for all 238 reports.
 *
 * The filters are the whole job. A report with no filter bar is a page that
 * either returns nothing or returns a year of the ledger, and ERPNext keeps
 * its filter definitions in JavaScript — so the gateway reads them out of the
 * script and this draws them with the same controls the forms use. A Link
 * filter is the same searchable picker as a Link field, because a person
 * should not have to learn two of them.
 *
 * Numbers are right-aligned and formatted by the column's own fieldtype, not
 * by guessing from the value: a "Balance" of 0 and a voucher number of 0 are
 * not the same kind of zero.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { EmptyByFilter, Failed, Loading, ScreenState } from '@jackdesk/shell'
import { FixButton } from './FixButton'
import type { ErpError, ExportKind } from './api'
import { useErp } from './context'
import { buildRegistry, renderWith } from './controls'
import { evaluateCondition } from './depends'
import {
  formatCurrency, formatDate, formatDatetime, formatNumber,
} from './format'
import type { DocField, ReportAnswer, ReportColumn, ReportDefinition } from './types'

export function ReportView({ name, onOpenDoc, onOpenReport, initialFilters }: {
  name: string
  /** Set by a drill-through, so the report opens already narrowed. */
  initialFilters?: Record<string, unknown>
  onOpenDoc?: (doctype: string, docname: string) => void
  /** Open another report with filters already set — how a figure reaches the
   *  entries behind it. */
  onOpenReport?: (report: string, filters: Record<string, unknown>) => void
}) {
  const erp = useErp()
  const [definition, setDefinition] = useState<ReportDefinition | null>(null)
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [answer, setAnswer] = useState<ReportAnswer | null>(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<ErpError | null>(null)
  const [downloading, setDownloading] = useState<ExportKind | null>(null)

  const registry = useMemo(() => buildRegistry(() => []), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true); setAnswer(null); setError(null); setDefinition(null)
    erp.reportDefinition(name)
      .then((d) => {
        if (cancelled) return
        setDefinition(d)
        // Defaults the script gave us; the rest stay empty until asked for.
        const start: Record<string, unknown> = {}
        for (const f of d.filters) if (f.default !== undefined) start[f.fieldname] = f.default
        // What the drill-through asked for wins over the script's defaults.
        for (const [k, v] of Object.entries(initialFilters ?? {})) if (v !== undefined) start[k] = v
        setValues(start)
      })
      .catch((e) => { if (!cancelled) setError(e as ErpError) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [erp, name, initialFilters])

  const missing = useMemo(() => {
    if (!definition) return []
    return definition.filters
      .filter((f) => f.reqd && !f.hidden)
      .filter((f) => { const v = values[f.fieldname]; return v === undefined || v === null || v === '' })
      .map((f) => f.label || f.fieldname)
  }, [definition, values])

  const run = useCallback(async () => {
    if (!definition || missing.length) return
    setRunning(true); setError(null)
    try {
      setAnswer(await erp.runReport(name, values))
    } catch (e) {
      setError(e as ErpError); setAnswer(null)
    } finally {
      setRunning(false)
    }
  }, [erp, name, definition, values, missing.length])

  // Run as soon as everything required is there. A report that makes you
  // press a button before it will show you anything, when it already has
  // everything it needs, is a report that wastes a click every single time.
  useEffect(() => { if (definition && !missing.length && !answer) void run() }, [definition, missing.length, answer, run])

  const save = async (kind: ExportKind) => {
    setDownloading(kind); setError(null)
    try { await erp.downloadReport(name, values, kind) }
    catch (e) { setError(e as ErpError) }
    finally { setDownloading(null) }
  }

  if (loading) return <div className="page"><Loading rows={6} label={`Loading ${name}`} /></div>
  if (!definition) return <div className="page"><Failed message={error?.message} requestId={error?.requestId} /></div>

  return (
    <div className="page" style={{ display: 'grid', gap: 14 }}>
      <div className="row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="small muted">{definition.module ?? 'Report'}</div>
          <h1>{definition.name}</h1>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {(['csv', 'xlsx', 'pdf'] as const).map((kind) => (
            <button key={kind} className="btn" disabled={!answer || downloading !== null}
                    onClick={() => void save(kind)}>
              {downloading === kind ? 'Preparing…'
                : kind === 'csv' ? 'Download CSV' : kind === 'xlsx' ? 'Download Excel' : 'PDF'}
            </button>
          ))}
        </div>
      </div>

      <section className="card card-pad">
        {definition.filters_are_a_fallback ? (
          <p className="small muted" style={{ marginTop: 0 }}>
            This report&rsquo;s own filters could not be read, so these are the ones most
            reports take. If it needs something else, it will say so when you run it.
          </p>
        ) : null}
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
          {definition.filters.filter((f) => !f.hidden).map((f) => {
            if (!evaluateCondition(f.depends_on, values)) return null
            return (
              <div key={f.fieldname} data-field={f.fieldname}>
                {renderWith(registry, {
                  field: normalise(f), value: values[f.fieldname], doc: values,
                  readOnly: false, required: Boolean(f.reqd),
                  onChange: (v) => { setValues((c) => ({ ...c, [f.fieldname]: v })); setAnswer(null) },
                })}
              </div>
            )
          })}
        </div>
        <div className="row" style={{ marginTop: 14, gap: 10 }}>
          <button className="btn btn-primary" onClick={run} disabled={running || missing.length > 0}>
            {running ? 'Running…' : answer ? 'Run again' : 'Run'}
          </button>
          {missing.length ? <span className="small muted">Needs {missing.join(', ')}</span> : null}
          {answer?.execution_time ? (
            <span className="small muted">{answer.total_rows.toLocaleString('en-IN')} rows in {answer.execution_time.toFixed(2)}s</span>
          ) : null}
        </div>
      </section>

      {error ? (
        <div className="card card-pad" role="alert" style={{ borderLeft: '3px solid var(--danger)' }}>
          <strong>{error.message}</strong>
          {error.requestId ? <div className="small muted mono">{error.requestId}</div> : null}
          {/* A refusal that names a setting gets a way to reach it. */}
          <div><FixButton fix={error.fix} onOpen={onOpenDoc} /></div>
        </div>
      ) : null}

      {answer?.report_summary?.length ? (
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
          {answer.report_summary.map((s, i) => (
            <div className="card card-pad" key={i}>
              <div className="small muted">{s.label}</div>
              <div style={{ fontSize: 20, fontWeight: 650, marginTop: 2 }}>
                {s.datatype === 'Currency' ? formatCurrency(s.value) : String(s.value ?? '')}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {running && !answer ? <Loading rows={8} label="Running" />
        : answer ? (
          answer.rows.length === 0
            ? <EmptyByFilter />
            : <ReportTable answer={answer} onOpenDoc={onOpenDoc}
                           onDrill={onOpenReport ? (account) => onOpenReport('General Ledger', {
                             company: values.company,
                             account: [account],
                             from_date: values.from_date ?? values.period_start_date,
                             to_date: values.to_date ?? values.period_end_date,
                           }) : undefined} />
        ) : !error && missing.length ? (
          <ScreenState title="Choose the filters above" detail={`This report needs ${missing.join(' and ')}.`} />
        ) : null}
    </div>
  )
}

function ReportTable({ answer, onOpenDoc, onDrill }: {
  answer: ReportAnswer
  onOpenDoc?: (d: string, n: string) => void
  /** A figure is only useful if you can get to what made it. */
  onDrill?: (account: string) => void
}) {
  // Financial statements come back as a tree: each row carries an `indent`,
  // and a Balance Sheet drawn flat is a list of account names with no shape.
  const isTree = answer.rows.some((r) => !Array.isArray(r) && (r as Record<string, unknown>).indent != null)
  const [shut, setShut] = useState<Set<number>>(new Set())

  const indentOf = (row: ReportAnswer['rows'][number]) =>
    Array.isArray(row) ? 0 : Number((row as Record<string, unknown>).indent ?? 0)

  /** A row is hidden when any ancestor above it is collapsed. */
  const visible = useMemo(() => {
    if (!isTree || shut.size === 0) return answer.rows.map((_, i) => i)
    const out: number[] = []
    let hideDeeperThan: number | null = null
    answer.rows.forEach((row, i) => {
      const indent = indentOf(row)
      if (hideDeeperThan !== null && indent > hideDeeperThan) return
      hideDeeperThan = null
      out.push(i)
      if (shut.has(i)) hideDeeperThan = indent
    })
    return out
  }, [answer.rows, isTree, shut])

  const hasChildren = (i: number) =>
    isTree && i + 1 < answer.rows.length && indentOf(answer.rows[i + 1]!) > indentOf(answer.rows[i]!)

  const totals = useMemo(() => {
    if (!answer.add_total_row) return null
    const out: Record<string, number> = {}
    for (const c of answer.columns) {
      if (!isNumeric(c) || !c.fieldname) continue
      out[c.fieldname] = answer.rows.reduce((n, r) =>
        n + (Number(Array.isArray(r) ? 0 : (r as Record<string, unknown>)[c.fieldname!]) || 0), 0)
    }
    return out
  }, [answer])

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {answer.truncated ? (
        <div className="small" style={{ padding: '8px 14px', background: 'var(--warn-soft)', color: 'var(--warn)' }}>
          Showing the first {answer.rows.length.toLocaleString('en-IN')} of{' '}
          {answer.total_rows.toLocaleString('en-IN')} rows. Download the file for all of them.
        </div>
      ) : null}
      <div style={{ overflowX: 'auto', maxHeight: '70vh' }}>
        <table className="tbl">
          <thead>
            <tr>{answer.columns.map((c, i) => (
              <th key={c.fieldname ?? i} className={isNumeric(c) ? 'num' : undefined}
                  style={{ minWidth: c.width ? Math.min(c.width, 240) : undefined }}>
                {c.label || c.fieldname}
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const row = answer.rows[r]!
              const indent = indentOf(row)
              const parent = hasChildren(r)
              return (
                <tr key={r} style={{ cursor: 'default' }}>
                  {answer.columns.map((c, i) => {
                    const value = Array.isArray(row) ? row[i] : row[c.fieldname ?? '']
                    const link = c.fieldtype === 'Link' && c.options && value
                    const first = i === 0
                    return (
                      <td key={c.fieldname ?? i} className={isNumeric(c) ? 'num' : 'truncate'}
                          style={{ maxWidth: 300,
                                   paddingLeft: first && isTree ? 12 + indent * 18 : undefined,
                                   fontWeight: parent || indent === 0 ? 600 : undefined }}>
                        {first && parent ? (
                          <button className="btn btn-quiet btn-sm" style={{ minHeight: 0, padding: '0 4px', marginRight: 4 }}
                                  aria-expanded={!shut.has(r)}
                                  aria-label={shut.has(r) ? 'Show what is inside' : 'Hide what is inside'}
                                  onClick={() => setShut((s) => {
                                    const next = new Set(s)
                                    next.has(r) ? next.delete(r) : next.add(r)
                                    return next
                                  })}>{shut.has(r) ? '+' : '−'}</button>
                        ) : null}
                        {link && onOpenDoc ? (
                          <span className="row" style={{ gap: 6 }}>
                            <a href="#" className="truncate"
                               onClick={(e) => { e.preventDefault(); onOpenDoc(c.options!, String(value)) }}>
                              {String(value)}
                            </a>
                            {/* Two clicks from a figure to the lines that
                                made it: the whole reason anybody opens a
                                trial balance. */}
                            {c.options === 'Account' && onDrill ? (
                              <button className="btn btn-quiet btn-sm" title="See the entries behind this"
                                      aria-label={`See the entries for ${value}`}
                                      style={{ minHeight: 0, padding: '0 4px' }}
                                      onClick={(e) => { e.stopPropagation(); onDrill(String(value)) }}>↳</button>
                            ) : null}
                          </span>
                        ) : cellText(value, c)}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
          {totals ? (
            <tfoot>
              <tr style={{ cursor: 'default', background: 'var(--sunk)' }}>
                {answer.columns.map((c, i) => (
                  <td key={c.fieldname ?? i} className={isNumeric(c) ? 'num' : undefined}
                      style={{ fontWeight: 650 }}>
                    {i === 0 ? 'Total' : (c.fieldname && totals[c.fieldname] !== undefined
                      ? cellText(totals[c.fieldname], c) : '')}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  )
}

const isNumeric = (c: ReportColumn) =>
  ['Int', 'Float', 'Currency', 'Percent'].includes(String(c.fieldtype))

function cellText(value: unknown, column: ReportColumn): string {
  if (value === null || value === undefined || value === '') return ''
  switch (column.fieldtype) {
    case 'Currency': return formatCurrency(value)
    case 'Float': return formatNumber(value)
    case 'Percent': return `${formatNumber(value, 2)}%`
    case 'Int': return formatNumber(value, 0)
    case 'Date': return formatDate(value)
    case 'Datetime': return formatDatetime(value)
    case 'Check': return value ? 'Yes' : 'No'
    default: return String(value)
  }
}

/** A report filter is nearly a DocField, but ERPNext uses a few fieldtypes in
 *  filters that do not exist as form fields. MultiSelectList is the common
 *  one; a Link picker is close enough and far better than nothing. */
function normalise(filter: DocField): DocField {
  const fieldtype = String(filter.fieldtype)
  if (fieldtype === 'MultiSelectList') return { ...filter, fieldtype: 'Link' }
  if (fieldtype === 'DateRange') return { ...filter, fieldtype: 'Date' }
  return filter
}
